import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ListTodo } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/task-lists/search')({
    staticData: {
        metadata: {
            title: 'Task lists',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <TaskListSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['taskList']['listTaskLists'];

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['taskList']['listTaskLists']>['rows'][number],
    NonNullable<QueryParams['orderBy']>
>();

function TaskListSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.taskList.deleteTaskList.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({});

    const { data, isPending, error, refetch, isFetching } = trpc.taskList.listTaskLists.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        urlKey: 'q',
        queryError: error,
        emptyState: { noRecordsTitle: 'No task lists yet' },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        <Space size="small" onClick={(e) => e.stopPropagation()}>
                            <Button.Link
                                to="/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks"
                                params={{ orgId, taskListId: row.id }}
                                variant="tertiary"
                                icon={<ListTodo />}
                            >
                                Manage tasks
                            </Button.Link>
                            <Button.Delete
                                onConfirm={(onSuccess) =>
                                    mutate(
                                        { id: row.id },
                                        {
                                            onSuccess() {
                                                onSuccess();
                                                void trpcUtils.taskList.invalidate();
                                            },
                                            onError(error) {
                                                errorMessage.show(error);
                                            },
                                        },
                                    )
                                }
                            />
                        </Space>
                    );
                },
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />
            <TableToolbar
                variant="enhanced"
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/claims/task-lists/search/add"
                        params={{
                            orgId,
                        }}
                        variant="primary"
                        label="New task list"
                    />
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/claims/task-lists/search/edit/$id',
                                params: {
                                    orgId,
                                    id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
